import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  MutableRefObject,
} from "react";
import { createPortal } from "react-dom";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";
export type TooltipAlign = "start" | "center" | "end";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  placement?: TooltipPlacement;
  align?: TooltipAlign;
  /** Extra classes merged onto the child element (e.g. "block min-w-0"). */
  className?: string;
  /** Only show when the child element is text-truncated. */
  onlyWhenTruncated?: boolean;
  /** Suppress the tooltip entirely. */
  disabled?: boolean;
  /** Show the tooltip even without hover (e.g. always-visible status). */
  forceVisible?: boolean;
  /** Delay in ms before showing on hover. Focus shows immediately. Default 150. */
  delay?: number;
  /** Allow wrapping with a constrained width. Default is single-line nowrap. */
  multiline?: boolean;
}

const GAP = 8;
const VIEWPORT_PAD = 8;
const DEFAULT_DELAY = 150;

const OPPOSITE: Record<TooltipPlacement, TooltipPlacement> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};

function mergeRefs<T>(
  ...refs: (React.Ref<T> | null | undefined)[]
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        (ref as MutableRefObject<T | null>).current = value;
      }
    });
  };
}

function isCoarsePointer(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(hover: none)").matches;
}

interface ResolvedCoords {
  top: number;
  left: number;
  placement: TooltipPlacement;
  /** Distance along the relevant tooltip edge where the arrow should point. */
  arrowOffset: number;
}

function positionFor(
  p: TooltipPlacement,
  align: TooltipAlign,
  a: DOMRect,
  t: { width: number; height: number },
): { top: number; left: number } {
  if (p === "top" || p === "bottom") {
    const top = p === "top" ? a.top - t.height - GAP : a.bottom + GAP;
    let left: number;
    if (align === "center") left = a.left + a.width / 2 - t.width / 2;
    else if (align === "end") left = a.right - t.width;
    else left = a.left;
    return { top, left };
  }
  const left = p === "left" ? a.left - t.width - GAP : a.right + GAP;
  let top: number;
  if (align === "center") top = a.top + a.height / 2 - t.height / 2;
  else if (align === "end") top = a.bottom - t.height;
  else top = a.top;
  return { top, left };
}

function fitsInViewport(
  c: { top: number; left: number },
  t: { width: number; height: number },
  vw: number,
  vh: number,
): boolean {
  return (
    c.top >= VIEWPORT_PAD &&
    c.left >= VIEWPORT_PAD &&
    c.top + t.height <= vh - VIEWPORT_PAD &&
    c.left + t.width <= vw - VIEWPORT_PAD
  );
}

function clampToViewport(
  c: { top: number; left: number },
  t: { width: number; height: number },
  vw: number,
  vh: number,
): { top: number; left: number } {
  return {
    top: Math.max(
      VIEWPORT_PAD,
      Math.min(c.top, vh - t.height - VIEWPORT_PAD),
    ),
    left: Math.max(
      VIEWPORT_PAD,
      Math.min(c.left, vw - t.width - VIEWPORT_PAD),
    ),
  };
}

const ARROW_HALF = 4;
const ARROW_INSET = 12;

function arrowOffsetFor(
  p: TooltipPlacement,
  c: { top: number; left: number },
  anchor: DOMRect,
  tip: { width: number; height: number },
): number {
  if (p === "top" || p === "bottom") {
    const triggerCenter = anchor.left + anchor.width / 2;
    const ideal = triggerCenter - c.left;
    return Math.max(
      ARROW_INSET,
      Math.min(ideal, tip.width - ARROW_INSET),
    );
  }
  const triggerCenter = anchor.top + anchor.height / 2;
  const ideal = triggerCenter - c.top;
  return Math.max(
    ARROW_INSET,
    Math.min(ideal, tip.height - ARROW_INSET),
  );
}

function resolveCoords(
  anchor: DOMRect,
  tip: { width: number; height: number },
  placement: TooltipPlacement,
  align: TooltipAlign,
  vw: number,
  vh: number,
): ResolvedCoords {
  const order: TooltipPlacement[] = [placement, OPPOSITE[placement]];
  if (placement === "top" || placement === "bottom") {
    order.push("right", "left");
  } else {
    order.push("bottom", "top");
  }
  for (const p of order) {
    const c = positionFor(p, align, anchor, tip);
    if (fitsInViewport(c, tip, vw, vh)) {
      return {
        ...c,
        placement: p,
        arrowOffset: arrowOffsetFor(p, c, anchor, tip),
      };
    }
  }
  const c = positionFor(placement, align, anchor, tip);
  const clamped = clampToViewport(c, tip, vw, vh);
  return {
    ...clamped,
    placement,
    arrowOffset: arrowOffsetFor(placement, clamped, anchor, tip),
  };
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = "top",
  align = "center",
  className,
  onlyWhenTruncated,
  disabled,
  forceVisible,
  delay = DEFAULT_DELAY,
  multiline,
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [coords, setCoords] = useState<ResolvedCoords | null>(null);
  const anchorRef = useRef<HTMLElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<number | null>(null);
  const reactId = useId();
  const tooltipId = `tooltip-${reactId.replace(/:/g, "")}`;

  useEffect(() => {
    setMounted(true);
  }, []);

  const cancelTimer = useCallback(() => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  const isTruncated = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return false;
    return el.scrollWidth > el.clientWidth;
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    if (isCoarsePointer()) return;
    if (onlyWhenTruncated && !isTruncated()) return;
    cancelTimer();
    if (delay <= 0) {
      setHovered(true);
    } else {
      showTimerRef.current = window.setTimeout(() => setHovered(true), delay);
    }
  }, [disabled, onlyWhenTruncated, isTruncated, cancelTimer, delay]);

  const handleMouseLeave = useCallback(() => {
    cancelTimer();
    setHovered(false);
  }, [cancelTimer]);

  const handleFocus = useCallback(() => {
    if (disabled) return;
    if (onlyWhenTruncated && !isTruncated()) return;
    cancelTimer();
    setFocused(true);
  }, [disabled, onlyWhenTruncated, isTruncated, cancelTimer]);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const visible =
    !disabled && !!content && (hovered || focused || !!forceVisible);

  // Hide on Escape, scroll, and resize while open.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        cancelTimer();
        setHovered(false);
        setFocused(false);
      }
    };
    const onScroll = () => {
      cancelTimer();
      setHovered(false);
      setFocused(false);
    };
    const onResize = () => {
      cancelTimer();
      setHovered(false);
      setFocused(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [visible, cancelTimer]);

  // Measure and pick the best placement once the tip is rendered.
  // `mounted` is in the deps so the measurement re-runs after the first
  // render (when `mounted` flips to true and the portal mounts the tip);
  // without it, forceVisible tooltips never measure and stay hidden.
  useLayoutEffect(() => {
    if (!visible) {
      setCoords(null);
      return;
    }
    const anchor = anchorRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    const a = anchor.getBoundingClientRect();
    const t = { width: tip.offsetWidth, height: tip.offsetHeight };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setCoords(resolveCoords(a, t, placement, align, vw, vh));
  }, [visible, placement, align, content, multiline, mounted]);

  useEffect(() => () => cancelTimer(), [cancelTimer]);

  // Clone the child to attach our handlers/ref/aria-describedby without an
  // extra wrapper element, so layout and dnd-kit measurements are unaffected.
  const child = React.Children.only(children);
  const existingRef = (child as unknown as { ref?: React.Ref<unknown> }).ref;
  const childProps = child.props as React.HTMLAttributes<HTMLElement> & {
    className?: string;
    "aria-describedby"?: string;
  };

  const enhancedChild = React.cloneElement(
    child as React.ReactElement<
      React.HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLElement> }
    >,
    {
      ref: mergeRefs(anchorRef, existingRef as React.Ref<HTMLElement>),
      className:
        [childProps.className, className].filter(Boolean).join(" ") ||
        undefined,
      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
        handleMouseEnter();
        childProps.onMouseEnter?.(e);
      },
      onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
        handleMouseLeave();
        childProps.onMouseLeave?.(e);
      },
      onFocus: (e: React.FocusEvent<HTMLElement>) => {
        handleFocus();
        childProps.onFocus?.(e);
      },
      onBlur: (e: React.FocusEvent<HTMLElement>) => {
        handleBlur();
        childProps.onBlur?.(e);
      },
      "aria-describedby":
        visible
          ? [childProps["aria-describedby"], tooltipId]
              .filter(Boolean)
              .join(" ")
          : childProps["aria-describedby"],
    },
  );

  const resolvedPlacement = coords?.placement ?? placement;

  // Arrow box, rotated 45deg, positioned on the side opposite the tooltip body
  // and offset along that side so it points at the trigger's center.
  const arrowBase = "absolute w-2 h-2 bg-warm-bg-card";
  let arrowClass = arrowBase;
  let arrowStyle: React.CSSProperties = { transform: "rotate(45deg)" };
  const arrowOffset = coords?.arrowOffset;
  if (resolvedPlacement === "top") {
    arrowClass += " border-b border-r border-warm-border";
    arrowStyle = {
      ...arrowStyle,
      bottom: -5,
      left: arrowOffset !== undefined ? arrowOffset - ARROW_HALF : "50%",
      marginLeft: arrowOffset !== undefined ? 0 : -ARROW_HALF,
    };
  } else if (resolvedPlacement === "bottom") {
    arrowClass += " border-t border-l border-warm-border";
    arrowStyle = {
      ...arrowStyle,
      top: -5,
      left: arrowOffset !== undefined ? arrowOffset - ARROW_HALF : "50%",
      marginLeft: arrowOffset !== undefined ? 0 : -ARROW_HALF,
    };
  } else if (resolvedPlacement === "left") {
    arrowClass += " border-t border-r border-warm-border";
    arrowStyle = {
      ...arrowStyle,
      right: -5,
      top: arrowOffset !== undefined ? arrowOffset - ARROW_HALF : "50%",
      marginTop: arrowOffset !== undefined ? 0 : -ARROW_HALF,
    };
  } else {
    arrowClass += " border-b border-l border-warm-border";
    arrowStyle = {
      ...arrowStyle,
      left: -5,
      top: arrowOffset !== undefined ? arrowOffset - ARROW_HALF : "50%",
      marginTop: arrowOffset !== undefined ? 0 : -ARROW_HALF,
    };
  }

  return (
    <>
      {enhancedChild}
      {visible &&
        mounted &&
        createPortal(
          <div
            ref={tipRef}
            id={tooltipId}
            role="tooltip"
            data-placement={resolvedPlacement}
            className={[
              "fixed z-50 px-3 py-2 bg-warm-bg-card border border-warm-border text-warm-text-secondary text-sm rounded-soft pointer-events-none shadow-soft-lg animate-tooltip-in",
              multiline ? "max-w-xs" : "whitespace-nowrap",
            ].join(" ")}
            style={{
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              visibility: coords ? "visible" : "hidden",
            }}
          >
            {content}
            <div className={arrowClass} style={arrowStyle} />
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
